import type { Clue, Puzzle } from "./types.ts";

const ABSENT = -1;
const POSITIONS = [0, 1, 2];
const DIGITS = Array.from({ length: 10 }, (_, digit) => digit);

type Domains = number[][];

export interface Deduction {
  digit: number;
  places: number[];
}

export interface SolutionStep {
  title: string;
  paragraphs: string[];
  clues: number[];
  deductions: Deduction[];
  code: (number | null)[];
  excluded: number[];
}

export interface WorkedSolution {
  steps: SolutionStep[];
  code: number[] | undefined;
  checks: { guess: string; note: string }[];
}

interface Proposal {
  title: string;
  reason: string;
  details?: string[];
  clues: number[];
  deductions: Deduction[];
}

function list(values: (string | number)[]): string {
  if (values.length < 2) return String(values[0] ?? "");
  return `${values.slice(0, -1).join(", ")} and ${values.at(-1)}`;
}

function place(position: number): string {
  return ["first", "in the middle", "last"][position]!;
}

function absent(domains: Domains, digit: number): boolean {
  return domains[digit]!.every((position) => position === ABSENT);
}

function included(domains: Domains, digit: number): boolean {
  return !domains[digit]!.includes(ABSENT);
}

function currentCode(domains: Domains): (number | null)[] {
  return POSITIONS.map((position) => DIGITS.find((digit) => domains[digit]!.length === 1 && domains[digit]![0] === position) ?? null);
}

function clueRule(clue: Clue): string {
  const total = clue.feedback.wellPlaced + clue.feedback.wronglyPlaced;
  const digits = clue.guess.join(" ");
  if (total === 0) return `The clue ${digits} says none of its digits is in the code.`;
  const count = total === 1 ? "one digit is" : "two digits are";
  if (clue.feedback.wellPlaced === 0) return `${digits}: exactly ${count} in the code, but ${total === 1 ? "it must move" : "both must move"}.`;
  if (clue.feedback.wronglyPlaced === 0) return `${digits}: exactly ${count} in the code, ${total === 1 ? "in the place shown" : "both in the places shown"}.`;
  return `${digits}: two digits are in the code. One stays in its place and the other must move.`;
}

function clueContext(clue: Clue, domains: Domains): string[] {
  const sentences: string[] = [];
  const excluded = clue.guess.filter((digit) => absent(domains, digit));
  if (excluded.length) sentences.push(`We have already crossed out ${list(excluded)}.`);
  for (const digit of clue.guess.filter((value) => !absent(domains, value))) {
    const choices = domains[digit]!.filter((position) => position !== ABSENT);
    if (choices.length === 1) {
      sentences.push(included(domains, digit)
        ? `${digit} is already ${place(choices[0]!)}.`
        : `If ${digit} is used, it can only go ${place(choices[0]!)}.`);
    } else {
      if (included(domains, digit)) sentences.push(`We already know ${digit} is in the code.`);
      const excludedPlaces = POSITIONS.filter((position) => !choices.includes(position));
      if (excludedPlaces.length) sentences.push(`${digit} cannot go ${list(excludedPlaces.map(place))}.`);
    }
  }
  return sentences;
}

function clueOptions(clue: Clue, domains: Domains): number[][] {
  const options: number[][] = [];
  const visit = (places: number[]): void => {
    if (places.length === clue.guess.length) {
      const total = places.filter((position) => position !== ABSENT).length;
      const right = places.filter((position, index) => position === index).length;
      if (total === clue.feedback.wellPlaced + clue.feedback.wronglyPlaced && right === clue.feedback.wellPlaced) options.push(places);
      return;
    }
    for (const position of domains[clue.guess[places.length]!]!) {
      if (position !== ABSENT && places.includes(position)) continue;
      visit([...places, position]);
    }
  };
  visit([]);
  return options;
}

function clueProposal(clue: Clue, clueIndex: number, domains: Domains): Proposal | undefined {
  const options = clueOptions(clue, domains);
  if (!options.length) return undefined;
  const deductions = clue.guess.flatMap((digit, index) => {
    const places = domains[digit]!.filter((position) => options.some((option) => option[index] === position));
    return places.length < domains[digit]!.length ? [{ digit, places }] : [];
  });
  if (!deductions.length) return undefined;
  return {
    title: clue.feedback.key === "none" ? `Cross out ${list(clue.guess)}` : `Look at ${clue.guess.join(" ")}`,
    reason: [clueRule(clue), ...clueContext(clue, domains)].join(" "),
    clues: [clueIndex],
    deductions,
  };
}

function globalProposals(domains: Domains): Proposal[] {
  const proposals: Proposal[] = [];
  const code = currentCode(domains);
  for (const [position, digit] of code.entries()) {
    if (digit === null) continue;
    const deductions = DIGITS.filter((other) => other !== digit && domains[other]!.includes(position))
      .map((other) => ({ digit: other, places: domains[other]!.filter((choice) => choice !== position) }));
    if (deductions.length) proposals.push({ title: `Keep ${digit} ${place(position)}`, reason: `${digit} fills that box. No other digit can use the same box.`, clues: [], deductions });
  }
  const required = DIGITS.filter((digit) => included(domains, digit));
  if (required.length === 3) {
    const deductions = DIGITS.filter((digit) => !required.includes(digit) && !absent(domains, digit)).map((digit) => ({ digit, places: [ABSENT] }));
    if (deductions.length) proposals.push({ title: "We have all three digits", reason: `${list(required)} must be in the code. There are only three boxes, so no other digits can be used.`, clues: [], deductions });
  }
  const available = DIGITS.filter((digit) => !absent(domains, digit));
  if (available.length === 3) {
    const deductions = available.filter((digit) => !included(domains, digit)).map((digit) => ({ digit, places: domains[digit]!.filter((position) => position !== ABSENT) }));
    if (deductions.length) proposals.push({ title: "Only three digits are left", reason: `Only ${list(available)} have not been crossed out. We need three different digits, so use all three.`, clues: [], deductions });
  }
  for (const position of POSITIONS) {
    if (code[position] !== null) continue;
    const possible = DIGITS.filter((digit) => domains[digit]!.includes(position));
    if (possible.length === 1) proposals.push({
      title: `Fill the ${["first", "middle", "last"][position]} box`,
      reason: `Only ${possible[0]} can go ${place(position)}. All the other digits have been ruled out of that box.`,
      clues: [], deductions: [{ digit: possible[0]!, places: [position] }],
    });
  }
  for (const [index, first] of required.entries()) {
    for (const second of required.slice(index + 1)) {
      const positions = [...new Set([...domains[first]!, ...domains[second]!])].sort((left, right) => left - right);
      if (positions.length !== 2) continue;
      const deductions = DIGITS.filter((digit) => digit !== first && digit !== second && domains[digit]!.some((position) => positions.includes(position)))
        .map((digit) => ({ digit, places: domains[digit]!.filter((position) => !positions.includes(position)) }));
      if (deductions.length) proposals.push({ title: `Leave space for ${first} and ${second}`, reason: `We need both ${first} and ${second}. They can only use the ${list(positions.map((position) => ["first", "middle", "last"][position]!))} boxes, so those boxes are reserved for them.`, clues: [], deductions });
    }
  }
  return proposals;
}

function conclusions(deductions: Deduction[], domains: Domains, omitRoutineMoves = false): string[] {
  const sentences: string[] = [];
  const excluded = deductions.filter((deduction) => deduction.places.length === 1 && deduction.places[0] === ABSENT).map((deduction) => deduction.digit);
  if (excluded.length) sentences.push(`Cross out ${list(excluded)}. ${excluded.length === 1 ? "It is" : "They are"} not in the code.`);
  for (const { digit, places } of deductions.filter((deduction) => !excluded.includes(deduction.digit))) {
    if (omitRoutineMoves && places.length > 1 && places.includes(ABSENT)) continue;
    const positions = places.filter((position) => position !== ABSENT);
    if (positions.length === 1) {
      sentences.push(places.includes(ABSENT)
        ? `If ${digit} is used, it must go ${place(positions[0]!)}.`
        : `Put ${digit} ${place(positions[0]!)}.`);
    } else {
      if (!places.includes(ABSENT) && domains[digit]!.includes(ABSENT)) sentences.push(`${digit} must be in the code.`);
      const removed = domains[digit]!.filter((position) => position !== ABSENT && !places.includes(position));
      if (removed.length) sentences.push(`${digit} cannot go ${list(removed.map(place))}.`);
    }
  }
  return sentences;
}

function priority(proposal: Proposal): number {
  return proposal.deductions.reduce((total, deduction) => total + (deduction.places.length === 1 ? deduction.places[0] === ABSENT ? 4 : 10 : !deduction.places.includes(ABSENT) ? 5 : 1), 0);
}

function nextDeduction(clues: readonly Clue[], domains: Domains): Proposal | undefined {
  const proposals = [
    ...clues.flatMap((clue, index) => {
      const proposal = clueProposal(clue, index, domains);
      return proposal ? [proposal] : [];
    }),
    ...globalProposals(domains),
  ].filter((proposal) => proposal.deductions.every((deduction) => deduction.places.length > 0));
  proposals.sort((left, right) => priority(right) - priority(left));
  return proposals[0];
}

function contradiction(clues: readonly Clue[], domains: Domains): { reason: string; clues: number[] } | undefined {
  const required = DIGITS.filter((digit) => included(domains, digit));
  const available = DIGITS.filter((digit) => !absent(domains, digit));
  if (required.length > 3) return { reason: `That would need ${required.length} different digits, but the code has only three.`, clues: [] };
  if (available.length < 3) return { reason: "That leaves too few digits to fill three different boxes.", clues: [] };
  for (const position of POSITIONS) {
    const fixed = required.filter((digit) => domains[digit]!.length === 1 && domains[digit]![0] === position);
    if (fixed.length > 1) return { reason: `Both ${list(fixed)} would need the ${["first", "middle", "last"][position]} box.`, clues: [] };
    if (!available.some((digit) => domains[digit]!.includes(position))) return { reason: `No digit would be left for the ${["first", "middle", "last"][position]} box.`, clues: [] };
  }
  for (const [index, clue] of clues.entries()) {
    if (clueOptions(clue, domains).length) continue;
    const total = clue.feedback.wellPlaced + clue.feedback.wronglyPlaced;
    const used = clue.guess.filter((digit) => included(domains, digit));
    const possible = clue.guess.filter((digit) => !absent(domains, digit));
    const prefix = `But ${clue.guess.join(" ")} `;
    if (used.length > total) return { reason: `${prefix}allows only ${total === 1 ? "one correct digit" : "two correct digits"}. ${list(used)} would ${used.length === 2 ? "both" : "all"} be in the code.`, clues: [index] };
    if (possible.length < total) return { reason: `${prefix}needs ${total === 1 ? "one correct digit" : "two correct digits"}. Too many of its digits would be crossed out.`, clues: [index] };
    const mustStay = clue.guess.filter((digit, position) => !absent(domains, digit) &&
      (included(domains, digit) || possible.length === total) && domains[digit]!.filter((choice) => choice !== ABSENT).every((choice) => choice === position));
    if (mustStay.length > clue.feedback.wellPlaced) return { reason: `${prefix}says ${clue.feedback.wronglyPlaced === 1 ? "one correct digit must move" : "the correct digits must move"}. ${list(mustStay)} would have to stay in ${mustStay.length === 1 ? "its" : "their"} shown ${mustStay.length === 1 ? "place" : "places"}.`, clues: [index] };
    return { reason: `${prefix}cannot then have ${clue.feedback.wellPlaced === 2 ? "two digits in the right places" : clue.feedback.wellPlaced === 1 ? "one digit in the right place" : "its correct digits in different places"}.`, clues: [index] };
  }
  return undefined;
}

function trialProposal(clues: readonly Clue[], domains: Domains): Proposal | undefined {
  let best: Proposal | undefined;
  for (const digit of DIGITS.filter((value) => domains[value]!.length > 1)) {
    for (const position of domains[digit]!) {
      const trial = domains.map((choices) => [...choices]);
      trial[digit] = [position];
      const assumption = position === ABSENT ? `Suppose ${digit} is not in the code.` : `Try putting ${digit} ${place(position)} for a moment.`;
      const reasons: string[] = [];
      const usedClues = new Set<number>();
      for (let turn = 0; turn < 8; turn += 1) {
        const conflict = contradiction(clues, trial);
        if (conflict) {
          conflict.clues.forEach((index) => usedClues.add(index));
          const proposal: Proposal = {
            title: position === ABSENT ? `Check whether we need ${digit}` : `Check where ${digit} can go`,
            reason: assumption,
            details: [...reasons, `${conflict.reason} So that cannot work.`],
            clues: [...usedClues],
            deductions: [{ digit, places: domains[digit]!.filter((choice) => choice !== position) }],
          };
          if (!best || [proposal.reason, ...proposal.details!].join(" ").length < [best.reason, ...best.details!].join(" ").length) best = proposal;
          break;
        }
        const next = nextDeduction(clues, trial);
        if (!next) break;
        next.clues.forEach((index) => usedClues.add(index));
        const rule = next.clues.length === 1 ? clueRule(clues[next.clues[0]!]!) : next.reason;
        reasons.push([rule, ...conclusions(next.deductions, trial, next.clues.length === 0)].join(" "));
        for (const deduction of next.deductions) trial[deduction.digit] = deduction.places;
      }
    }
  }
  return best;
}

function checkClue(clue: Clue, code: readonly number[]): { guess: string; note: string } {
  const right = clue.guess.filter((digit, position) => code[position] === digit);
  const wrong = clue.guess.filter((digit, position) => code.includes(digit) && code[position] !== digit);
  const sentences: string[] = [];
  if (right.length) sentences.push(`${list(right)} ${right.length === 1 ? "is in the right place" : "are in the right places"}.`);
  if (wrong.length) sentences.push(`${list(wrong)} ${wrong.length === 1 ? "is" : "are"} in the code but must move.`);
  return { guess: clue.guess.join(""), note: sentences.join(" ") || "None of these digits is in the code." };
}

export function explainPuzzle({ clues }: Pick<Puzzle, "clues">): WorkedSolution {
  if (clues.some((clue) => clue.guess.length !== 3 || new Set(clue.guess).size !== 3)) return { steps: [], code: undefined, checks: [] };
  const domains: Domains = DIGITS.map(() => [ABSENT, ...POSITIONS]);
  const steps: SolutionStep[] = [];
  for (let turn = 0; turn < 40; turn += 1) {
    const code = currentCode(domains);
    if (code.every((digit): digit is number => digit !== null)) return { steps, code, checks: clues.map((clue) => checkClue(clue, code)) };
    const next = nextDeduction(clues, domains) ?? trialProposal(clues, domains);
    if (!next) return { steps, code: undefined, checks: [] };
    const paragraphs = [next.reason, ...next.details ?? [], conclusions(next.deductions, domains, next.clues.length === 0).join(" ")].filter(Boolean);
    for (const deduction of next.deductions) domains[deduction.digit] = deduction.places;
    steps.push({ title: next.title, paragraphs, clues: next.clues, deductions: next.deductions, code: currentCode(domains), excluded: DIGITS.filter((digit) => absent(domains, digit)) });
  }
  return { steps, code: undefined, checks: [] };
}
